import { createRouter, createWebHistory, RouterView } from 'vue-router';
import { i18n, loadLocaleMessages } from './main.js'; // Import the i18n instance
import { routeGuard, startDatePT, endDatePT } from './routeGuards';

import Home from './Pages/Home/HomePage.vue';
import Enter from './Pages/Upload/Upload.vue';
import FAQ from './Pages/Support/Support.vue';
import Prizes from './Pages/Prizes/Prizes.vue';
import PreProgram from './Pages/Home/Homepagecomponents/PreProgram.vue';
import PostProgram from './Pages/Home/Homepagecomponents/PostProgram.vue';
import ReceiptTroubleshooting from './Pages/ReceiptTroubleshooting/ReceiptTroubleshooting.vue';
import QR from './Pages/QR/QR.vue';

const baseRoutes = [
  {
    path: '',
    redirect: to => ({ name: 'Home', params: to.params, query: to.query }),
  },
  {
    path: 'Home',
    name: 'Home',
    component: Home,
    meta: { titleKey: 'Home' }, // Use titleKey to reference the title
    props: { startDate: startDatePT, endDate: endDatePT },
  },
  {
    path: 'Enter',
    name: 'Enter',
    component: Enter,
    meta: { titleKey: 'Upload' },
  },
  {
    path: 'FAQ',
    name: 'FAQ',
    component: FAQ,
    meta: { titleKey: 'Support' },
  },
  {
    path: 'Prizes',
    name: 'Prizes',
    component: Prizes,
    meta: { titleKey: 'Prizes' },
  },
  {
    path: 'QR',
    name: 'QR',
    component: QR,
    meta: { titleKey: 'QR' }, // Use titleKey to reference the title
    beforeEnter: (to, from, next) => {
      if (to.params.locale === 'fr') {
        next({ name: 'Home', params: to.params, query: to.query });
      } else {
        next();
      }
    }
  },
  {
    path: 'ReceiptTroubleshooting',
    name: 'ReceiptTroubleshooting',
    component: ReceiptTroubleshooting, // our.domain/Faq
    meta: { titleKey: 'ReceiptTroubleshooting' },
  },
  {
    path: 'PreProgram',
    name: 'PreProgram',
    component: PreProgram,
    meta: { titleKey: 'PreProgram', hideNav: true },
    props: { startDate: startDatePT },
  },
  {
    path: 'PostProgram',
    name: 'PostProgram',
    component: PostProgram,
    meta: { titleKey: 'PostProgram', hideNav: true },
    props: { endDate: endDatePT },
  },
  { path: ':notFound(.*)', redirect: to => ({ name: 'Home', params: to.params, query: to.query }) },
];

const routes = [
  {
    path: '/fr',
    redirect: '/fr/Home'
  },
  {
    path: '/:locale(fr)?',
    component: RouterView,
    children: baseRoutes
  }
];

// Create the router
const router = createRouter({
  linkActiveClass: 'active',
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0 };
    }
  },
});

// Dynamically set language and page title based on route
router.beforeEach(async (to, from, next) => {
  const paramLocale = to.params.locale === 'fr' ? 'fr' : 'en';
  document.documentElement.lang = paramLocale;

  if (i18n.global.locale.value !== paramLocale) {
    await loadLocaleMessages(paramLocale);
    i18n.global.locale.value = paramLocale;
    localStorage.setItem('locale', paramLocale);
  }

  const pageTitleKey = to.meta.titleKey; // Access the title key
  if (pageTitleKey) {
    const translatedTitle = i18n.global.t(`titles.${pageTitleKey}`); // Fetch translated title using i18n
    document.title = translatedTitle || 'Default Title'; // Set the page title dynamically
  }

  const metaTags = {
    en: {
      'description': 'Choose your next adventure with Zuke’s® natural dog treats! These treats contain no corn, wheat, or soy, or artificial flavors.',
      'og:title': 'Enter to win Zuke’s® for a year! Choose your adventure with Zuke’s® natural dog treats!',
      'og:description': 'Choose your next adventure with Zuke’s® natural dog treats! These treats contain no corn, wheat, or soy, or artificial flavors.',
      'og:image': 'https://snippcheck.blob.core.windows.net/files/Nestle_2008881_Purina_CA_Zukes/meta-en.jpg',
      'og:url': 'https://www.winzukesforayear.com/',
    },
    fr: {
      'description': 'Choisissez votre prochaine aventure avec les gâteries naturelles pour chiens Zuke’sMD! Ces gâteries ne contiennent ni maïs, ni blé, ni soja, ni arômes artificiels.',
      'og:title': 'Participez pour courir la chance de gagner des produits Zuke’sMD pour un an! Choisissez votre aventure avec les gâteries naturelles pour chiens Zuke’sMD!',
      'og:description': 'Choisissez votre prochaine aventure avec les gâteries naturelles pour chiens Zuke’sMD! Ces gâteries ne contiennent ni maïs, ni blé, ni soja, ni arômes artificiels.',
      'og:image': 'https://snippcheck.blob.core.windows.net/files/Nestle_2008881_Purina_CA_Zukes/meta-fr.jpg',
      'og:url': 'https://www.winzukesforayear.com/fr',
    }
  };

  const tags = metaTags[paramLocale];
  let currentRef = document.querySelector('title');
  for (const [property, content] of Object.entries(tags)) {
    const attrName = property === 'description' ? 'name' : 'property';
    let element = document.querySelector(`meta[${attrName}="${property}"]`);
    if (!element) {
      element = document.createElement('meta');
      element.setAttribute(attrName, property);
      if (currentRef) {
        currentRef.parentNode.insertBefore(element, currentRef.nextSibling);
      } else {
        document.head.appendChild(element);
      }
    }
    element.setAttribute('content', content);
    currentRef = element;
  }

  next();
});

router.beforeEach(routeGuard);

export default router;
